import { clearFilterValues } from "./clearFilterValues";
import {
  BooleanFilter,
  Column,
  DateFilter,
  Filter,
  NumberFilter,
  TaxonomyFilter,
  TextFilter,
} from "./types";

// --- Filter detection ---

export const isFilteredTaxonomy = (f: TaxonomyFilter) => f.value.length > 0;

export const isFilteredDate = (f: DateFilter) => {
  if (f.operator === "isBetween") {
    return !!f.date1 && !!f.date2;
  }
  return !!f.date1;
};

export const isFilteredNumber = (f: NumberFilter) => {
  if (f.operator === "between") {
    return (
      f.number1 !== null &&
      f.number2 !== null &&
      f.number1 !== undefined &&
      f.number2 !== undefined &&
      f.number1 !== ("" as any) &&
      f.number2 !== ("" as any)
    );
  }
  return (
    f.number1 !== null && f.number1 !== undefined && f.number1 !== ("" as any)
  );
};

export const isFilteredText = (f: TextFilter) => f.value.length > 0;

const isFilteredByType: Record<string, (f: any) => boolean> = {
  number: isFilteredNumber,
  text: isFilteredText,
  date: isFilteredDate,
  taxonomy: isFilteredTaxonomy,
  boolean: (f: BooleanFilter) =>
    typeof f.unfilteredValue === "undefined"
      ? f.value !== null
      : f.value !== f.unfilteredValue,
};

export const isColumnFiltered = (f: Filter | undefined | null): boolean => {
  if (!f) return false;
  return isFilteredByType[f.type]?.(f) ?? false;
};

// --- Clear filter ---

export const clearFilter = (f: Filter | undefined | null): Filter | null => {
  if (!f) return f as null;
  if (f.type === "boolean") {
    return {
      ...f,
      value: typeof f.unfilteredValue === "undefined" ? null : f.unfilteredValue,
    };
  }
  return {
    ...f,
    ...clearFilterValues[f.type],
  } as Filter;
};

// --- In-memory filter application ---

const applyTextFilterToValue = (value: any, filter: TextFilter): boolean => {
  if (!isFilteredText(filter)) return true;
  if (value === null || value === undefined) return false;
  return String(value).toLowerCase().includes(filter.value.toLowerCase().trim());
};

const applyNumberFilterToValue = (
  value: any,
  filter: NumberFilter
): boolean => {
  if (!isFilteredNumber(filter)) return true;
  if (value === null || value === undefined) return false;
  const numValue = Number(value);
  if (isNaN(numValue)) return false;

  switch (filter.operator) {
    case "eq":
      return numValue === filter.number1;
    case "gt":
      return filter.includeNumber1
        ? numValue >= (filter.number1 ?? -Infinity)
        : numValue > (filter.number1 ?? -Infinity);
    case "lt":
      return filter.includeNumber1
        ? numValue <= (filter.number1 ?? Infinity)
        : numValue < (filter.number1 ?? Infinity);
    case "between": {
      const lower = filter.number1 ?? -Infinity;
      const upper = filter.number2 ?? Infinity;
      const passLower = filter.includeNumber1
        ? numValue >= lower
        : numValue > lower;
      const passUpper = filter.includeNumber2
        ? numValue <= upper
        : numValue < upper;
      return passLower && passUpper;
    }
    default:
      return true;
  }
};

const applyDateFilterToValue = (value: any, filter: DateFilter): boolean => {
  if (!isFilteredDate(filter)) return true;
  if (value === null || value === undefined) return false;

  const dateValue = new Date(value);
  if (isNaN(dateValue.getTime())) return false;

  const filterDate1 = filter.date1 ? new Date(filter.date1) : null;
  const filterDate2 = filter.date2 ? new Date(filter.date2) : null;

  switch (filter.operator) {
    case "isExact":
      if (!filterDate1) return true;
      return dateValue.toDateString() === filterDate1.toDateString();
    case "isAfter":
      if (!filterDate1) return true;
      return dateValue > filterDate1;
    case "isBefore":
      if (!filterDate1) return true;
      return dateValue < filterDate1;
    case "isBetween":
      if (!filterDate1 || !filterDate2) return true;
      return dateValue >= filterDate1 && dateValue <= filterDate2;
    default:
      return true;
  }
};

const applyBooleanFilterToValue = (
  value: any,
  filter: BooleanFilter
): boolean => {
  const isActive =
    typeof filter.unfilteredValue === "undefined"
      ? filter.value !== null
      : filter.value !== filter.unfilteredValue;
  if (!isActive) return true;
  return value === filter.value;
};

const applyTaxonomyFilterToValue = (
  value: any,
  filter: TaxonomyFilter
): boolean => {
  if (!isFilteredTaxonomy(filter)) return true;
  if (value === null || value === undefined) return false;
  return filter.value.includes(Number(value));
};

const applyFilterToValue = (value: any, filter: Filter): boolean => {
  switch (filter.type) {
    case "text":
      return applyTextFilterToValue(value, filter);
    case "number":
      return applyNumberFilterToValue(value, filter);
    case "date":
      return applyDateFilterToValue(value, filter);
    case "boolean":
      return applyBooleanFilterToValue(value, filter);
    case "taxonomy":
      return applyTaxonomyFilterToValue(value, filter);
    default:
      return true;
  }
};

/**
 * Apply in-memory filters from Column[] to a dataset.
 * @param data - The dataset to filter
 * @param columns - Column definitions with filter state
 * @param getFieldValue - Optional custom field accessor. Defaults to row[column.key]
 */
/**
 * Get active filters from Column[] as a map of columnKey -> Filter.
 * Useful for custom filter application (e.g. tree-aware filtering).
 */
export const getActiveFiltersFromColumns = (
  columns: Column[]
): Record<string, Filter> => {
  const result: Record<string, Filter> = {};
  for (const col of columns) {
    if (col.filter && isColumnFiltered(col.filter)) {
      result[col.key] = col.filter;
    }
  }
  return result;
};

/**
 * Check if a single value passes a filter.
 */
export { applyFilterToValue };

/**
 * Check if there are any active filters in a Column[].
 */
export const hasActiveColumnFilters = (columns: Column[]): boolean => {
  return columns.some((c) => c.filter && isColumnFiltered(c.filter));
};

/**
 * Apply in-memory filters from Column[] to a dataset.
 * @param data - The dataset to filter
 * @param columns - Column definitions with filter state
 * @param getFieldValue - Optional custom field accessor. Defaults to row[column.key]
 */
export const applyInMemoryFilters = <T extends Record<string, any>>(
  data: T[],
  columns: Column[],
  getFieldValue?: (row: T, columnKey: string) => any
): T[] => {
  const activeFilterColumns = columns.filter(
    (c) => c.filter && isColumnFiltered(c.filter)
  );

  if (activeFilterColumns.length === 0) return data;

  return data.filter((row) =>
    activeFilterColumns.every((col) => {
      const value = getFieldValue
        ? getFieldValue(row, col.key)
        : row[col.key];
      return applyFilterToValue(value, col.filter!);
    })
  );
};
